import { useCallback } from 'react';
import axios, { AxiosRequestConfig } from 'axios';
import config from '../utils/config';
import { useAuth } from '../context/AuthContext';

// Use the centralized config system instead of hardcoded localhost fallback
const API_BASE_URL = config.api.module1;

interface ApiClientOptions extends AxiosRequestConfig {
  body?: string; // Add body as a supported option
}

export const useApiClient = () => {
  const { getAccessToken } = useAuth();
  const callApi = useCallback(async <T = any>(
    endpoint: string,
    options: ApiClientOptions = {}
  ): Promise<T> => {
    try {
      let baseUrl = API_BASE_URL;
      
      // Apply the same URL transformation logic as other API clients
      if (config.environment.isContainer && !baseUrl.includes('localhost')) {
        // In container environment, use the URL as-is
        console.log('Container environment detected, using URL as-is:', baseUrl);
      } else if (baseUrl.startsWith('http://') || baseUrl.startsWith('https://')) {
        try {
          const url = new URL(baseUrl);
          
          // Handle container hostnames by converting to localhost for browser environment
          const portMap: { [key: string]: string } = {
            'module1-keyword-analysis:8000': '8001',
            'module2-simplified:8000': '8002',
            'module4-batch-processing:8000': '8004',
            'module2-classifier-codegen:8000': '8006',
            'module3-semantic-classification:8000': '8001'
          };
          
          const hostPort = `${url.hostname}:${url.port}`;
          
          if (portMap[hostPort]) {
            console.log(`Port mapping found: ${hostPort} → localhost:${portMap[hostPort]}`);
            url.hostname = 'localhost';
            url.port = portMap[hostPort];
          } else if (url.hostname !== 'localhost' && url.hostname !== '127.0.0.1') {
            console.log(`Non-localhost hostname detected: ${url.hostname}, converting to localhost`);
            url.hostname = 'localhost';
          }
          
          baseUrl = url.toString();
          console.log('Transformed API URL for useApiClient:', baseUrl);
        } catch (error) {
          console.error('Error transforming API URL in useApiClient:', error);
        }
      }
      
      // Fix URL concatenation to avoid double slashes
      const normalizedBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
      const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
      const url = `${normalizedBaseUrl}${normalizedEndpoint}`;
      
      // Extract body from options and map it to data for axios
      const { body, ...axiosOptions } = options;
      
      const requestConfig: AxiosRequestConfig = {
        url,
        data: body, // Map body to data for axios
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
        ...axiosOptions,
      };

      // Add API key if enabled
      if (config.api.keyEnabled && config.api.key) {
        requestConfig.headers!['X-API-Key'] = config.api.key;
      }

      // Attach bearer token from AuthGuard if available
      try {
        const token = await getAccessToken();
        if (token) {
          requestConfig.headers!['Authorization'] = `Bearer ${token}`;
        }
      } catch {
        // ignore
      }
      
      const response = await axios(requestConfig);
      
      return response.data;
    } catch (error: any) {
      console.error('API call failed:', error);
      
      // Re-throw with a more user-friendly message
      if (error.response) {
        // Server responded with an error
        throw new Error(
          error.response.data?.detail || 
          error.response.data?.message || 
          `API error: ${error.response.status}`
        );
      } else if (error.request) {
        // Request was made but no response
        throw new Error('Network error: Unable to reach the server');
      } else {
        // Something else happened
        throw new Error(error.message || 'An unexpected error occurred');
      }
    }
  }, []);
  
  return {
    callApi,
    apiBaseUrl: API_BASE_URL,
  };
}; 